import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { InView } from 'react-intersection-observer'
import cn from 'classnames'
import { ToastContainer } from 'react-toastify'
// Layout
import Footer from '@/components/newFooter'
import Header from '@/components/newHeader/Header'
import SignupModal from '@/components/signupModal'
// Puppy Profile
import AboutBreed from '@/components/puppyProfile/AboutBreed'
import Breeder from '@/components/puppyProfile/Breeder'
import Certificates from '@/components/puppyProfile/Certificates'
import Datasheet from '@/components/puppyProfile/Datasheet'
import FinancingModal from '@/components/puppyProfile/FinancingModal'
import HealthGuarantee from '@/components/puppyProfile/HealthGuarantee'
import Parents from '@/components/puppyProfile/Parents'
import PetImage from '@/components/puppyProfile/PetImage'
import Keypad from '@/components/puppyProfile/Keypad'
import MobileKeypad from '@/components/puppyProfile/MobileKeypad'
import OtherDogs from '@/components/puppyProfile/OtherDogs'
import Shipment from '@/components/puppyProfile/Shipment'
import Why from '@/components/puppyProfile/Why'
import Icons from '@/components/puppyProfile/lib/icons'
// Utils
import { convertGender } from '@/components/puppyProfile/lib/utils'
import { getNoTrackUser } from '@/utils/notrackuser'
import useMediaQuery from '@/utils/publicApi'
import { getToken, getUserData } from '@/utils/tokenStorage'
import { initializeApollo } from '@/utils/apolloClient'
// GQL Queries
import {
  CREATE_DOGVISIT,
  GET_BREED_DETAILS,
  GET_CUSTOMER_FAVOURITES,
  GET_DOG_DETAIL,
  GET_FAV_DOG,
  UPDATE_FAVOURITE,
  PUPPY_SEARCH,
} from '@/graphql/index'

import styles from './[id].module.scss'
import Skeleton from '@/components/puppyProfile/Skeleton'

export const config = { unstable_runtimeJS: true }

export default function PuppyProfile({
  isDiscountBannerClosed,
  setIsDiscountBannerClosed,
  dataDog,
  breedDetails,
  allBreedPuppies,
}) {
  const router = useRouter()
  const isMobile = useMediaQuery('(max-width:600px)')
  const isTablet = useMediaQuery('(max-width:1270px)')

  const [userData, setUserData] = useState(null)
  const [selectedDog, setSelectedDog] = useState(null)
  const [visibility, setVisibility] = useState({
    showSignup: false,
    showFinancingModal: false,
    isPetImageVisible: true,
    isKeypadVisible: true,
  })

  const data = dataDog
  const dogId = dataDog?.dog?.id
  const loading = router.isFallback

  // Dog data
  const dog = data?.dog || {}
  const salePrice = dog?.salePrice?.subTotal || {}
  let gender = convertGender(dog?.gender)
  if (Array.isArray(gender)) gender = gender.join(' ')

  const remainingBreeds = allBreedPuppies?.search?.rows.filter(
    (elem) => elem.id !== dogId,
  )
  const breedDogs = {
    id: dog?.breed?.id,
    name: dog?.breed?.name,
    slug: dog?.breed?.slug,
    dogs: remainingBreeds?.slice(0, 10),
  }

  const { data: dataFav } = useQuery(GET_FAV_DOG, {
    variables: { id: dogId },
    skip: !dogId,
  })
  const favs = dataFav?.favouriteDogCount?.count

  const { data: dataApp, loading: loadingData } = useQuery(GET_DOG_DETAIL, {
    variables: { selected: parseInt(dogId), slug: data?.dog?.slug },
  })
  const infoUpdated = dataApp?.dog

  const { data: favouriteData } = useQuery(GET_CUSTOMER_FAVOURITES, {
    skip: !userData,
  })

  const [updateFavourite] = useMutation(UPDATE_FAVOURITE, {
    refetchQueries: [{ query: GET_CUSTOMER_FAVOURITES }],
  })

  // Get user data
  useEffect(() => {
    const _userData = getUserData(getToken())
    if (_userData) setUserData(_userData)
  }, [dataApp])

  let hasApplication, dataApplication

  // Handle Application data
  if (infoUpdated) {
    hasApplication = infoUpdated.dogApplications.filter(
      (elem) => elem.customerId === userData?.id && !elem.canceled,
    )
    dataApplication = hasApplication?.[0]
  }

  // Is favorite?
  const handleUpdateFavourite = () => {
    if (userData) {
      updateFavourite({
        variables: { dog: dog?.id },
      })
    } else {
      setVisibility((prevState) => ({ ...prevState, showSignup: true }))
      setSelectedDog(dog.id)
    }
  }

  // Dog Visit - set dog id to dog visit table
  const [createDogVisit] = useMutation(CREATE_DOGVISIT)
  useEffect(() => {
    const noTackUser = getNoTrackUser()
    // create dog view count if the user is not a breeder or a admin
    if (!noTackUser) {
      createDogVisit({ variables: { dog: dog?.id } })
    }
  }, [dog.id])

  return (
    <>
      <Head>
        {/*  Facebook tags  */}
        <meta
          name="og:title"
          content={`${dog?.name} | ${dog?.breed?.name} ${convertGender(
            dog?.gender,
          )} | Puppy ID ${dog.id}`}
          key="title"
        />
        <meta name="og:type" content="website" key="type" />
        <meta
          name="og:url"
          content={`${process.env.NEXT_PUBLIC_ROOT_URL}/puppy/${dog?.slug}`}
          key="url"
        />
        <meta
          name="og:image"
          content={`${process.env.NEXT_PUBLIC_MEDIA_BUCKET}${dog?.profilePicture?.medium}`}
          key="image"
        />
        <meta
          name="og:image:url"
          content={`${process.env.NEXT_PUBLIC_MEDIA_BUCKET}${dog?.profilePicture?.medium}`}
          key="imageUrl"
        />
        <meta
          name="og:description"
          content={dog?.description}
          key="description"
        />

        {/*  standard tags  */}
        <title>
          {data
            ? `${dog?.name} | ${dog?.breed?.name} ${gender} | Puppy ID ${dog?.id}`
            : 'Loading Puppy Profile...'}
        </title>

        <meta
          name="robots"
          content={
            process.env.NEXT_PUBLIC_ROOT_URL ===
            'https://qa-front.friendapuppy.com'
              ? 'noindex, nofollow'
              : 'index, follow'
          }
        />
        <meta name="description" content={dog?.description} />
        <meta name="theme-color" content="#ffffff" />
        <meta charSet="UTF-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, user-scalable=0"
        />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_ROOT_URL}/puppy/${dog?.slug}`}
        />
      </Head>

      <main className={styles.main}>
        <ToastContainer
          position="bottom-left"
          autoClose={false}
          closeButton={false}
          closeOnClick={false}
          limit={1}
        />

        <Header
          isDiscountBannerClosed={isDiscountBannerClosed}
          setIsDiscountBannerClosed={setIsDiscountBannerClosed}
        />

        {!visibility.isKeypadVisible && (
          <MobileKeypad
            dog={dog}
            salePrice={salePrice}
            loading={loading}
            dataApplication={dataApplication}
          />
        )}

        <div className={styles.content}>
          {loading || loadingData ? (
            <Skeleton />
          ) : (
            <>
              <div
                className={
                  dog.isAvailable
                    ? styles.paginationHead
                    : styles.paginationHeadTop
                }
              >
                <p className={styles.steps}>
                  <a
                    className={styles.step}
                    href={`${process.env.NEXT_PUBLIC_ROOT_URL}/search?sort=by-featured`}
                  >
                    All Puppies{' '}
                  </a>
                  <span className={styles.arrowRight} />{' '}
                  <a
                    className={styles.step}
                    href={`${process.env.NEXT_PUBLIC_ROOT_URL}/search?breed=${dog?.breed?.slug}`}
                  >
                    {' '}
                    {dog?.breed?.name}
                  </a>{' '}
                  <span className={styles.arrowRight} />{' '}
                  <a className={styles.step}>{dog?.name}</a>{' '}
                </p>

                <Icons {...{ dog }} />
              </div>

              {isMobile && (
                <button
                  onClick={() => window.history.go(-1)}
                  className={cn({
                    [styles.btnBack]: true,
                    [styles.hasBanner]: !dog?.isAvailable,
                  })}
                >
                  <img
                    className={styles.arrowLeftBack}
                    src="/puppy/arrow-left-black-large.svg"
                    alt="arrow left"
                  />
                </button>
              )}

              <InView
                onChange={(inView) =>
                  setVisibility((curr) => ({
                    ...curr,
                    isPetImageVisible: inView,
                  }))
                }
              >
                <PetImage dog={dog} isMobile={isMobile} />
              </InView>

              <div className={styles.detail}>
                <div className={styles.leftPane}>
                  <InView
                    onChange={(inView) =>
                      setVisibility((curr) => ({
                        ...curr,
                        isKeypadVisible: inView,
                      }))
                    }
                  >
                    <Datasheet
                      {...{
                        isMobile,
                        dog,
                        favs,
                        breedDetails,
                        salePrice,
                        dataApplication,
                      }}
                      isFavourite={
                        !!favouriteData?.customer?.favourites?.filter(
                          (d) => d.id === dog?.id,
                        ).length
                      }
                      updateFavourite={handleUpdateFavourite}
                      updatedData={infoUpdated}
                    />

                    {isTablet && (
                      <Keypad
                        dog={dog}
                        salePrice={salePrice}
                        dataApplication={dataApplication}
                        updatedData={infoUpdated}
                      />
                    )}
                  </InView>

                  <HealthGuarantee application={dog.breeder.application} />

                  {(infoUpdated?.litter?.mom || infoUpdated?.litter?.dad) && (
                    <Parents dog={infoUpdated} />
                  )}

                  {dog?.breeder?.id && (
                    <Certificates dog={dog} isMobile={isMobile} />
                  )}

                  <Shipment dog={dog} isTablet={isTablet} />

                  <Why size="small" />

                  {dog?.breeder?.id && (
                    <Breeder dog={dog} isMobile={isMobile} />
                  )}

                  {dog?.breed?.description && (
                    <AboutBreed breed={dog.breed} breedDetails={breedDetails} />
                  )}
                </div>

                <div className={styles.rightPane}>
                  <Keypad
                    dog={dog}
                    showImage={!visibility.isPetImageVisible}
                    salePrice={salePrice}
                    dataApplication={dataApplication}
                    updatedData={infoUpdated}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        <div className={styles.divider} />

        <OtherDogs
          breedId={dog?.breed?.id}
          breedDogs={breedDogs}
          favourites={favouriteData?.customer?.favourites || []}
          updateFavourite={handleUpdateFavourite}
          isMobile={isMobile}
        />

        <Footer />

        <SignupModal
          isOpen={visibility.showSignup}
          close={() =>
            setVisibility((prevState) => ({ ...prevState, showSignup: false }))
          }
          successRedirect={`/puppy/${dog.id}`}
          success={() => {
            setVisibility((prevState) => ({ ...prevState, showSignup: false }))
            setUserData(getUserData(getToken()))
            updateFavourite({
              variables: { dog: selectedDog },
            })
          }}
        />

        {visibility.showFinancingModal ? (
          <FinancingModal
            setShowFinancingModal={() =>
              setVisibility((prevState) => ({
                ...prevState,
                showFinancingModal: false,
              }))
            }
          />
        ) : null}
      </main>
    </>
  )
}

export async function getStaticProps({ params }) {
  const apolloClient = initializeApollo()
  const notFound = { notFound: true }
  if (!params?.id) return notFound
  // Handle puppy select by id or name slug
  let variables

  if (isNaN(params?.id)) {
    variables = { slug: params?.id }
  } else {
    variables = { id: parseInt(params?.id) }
  }

  let dataDog
  let breedDetails
  let allBreedPuppies

  try {
    const { data } = await apolloClient.query({
      // fetchPolicy: 'no-cache',
      query: GET_DOG_DETAIL,
      variables: {
        selected: variables?.id,
        slug: variables?.slug,
      },
    })
    dataDog = data

    if (!isNaN(params?.id) && data?.dog?.slug) {
      return {
        redirect: {
          destination: `/puppy/${data?.dog?.slug}`,
          statusCode: 301,
        },
      }
    }

    const { data: dataBreed } = await apolloClient.query({
      fetchPolicy: 'no-cache',
      query: GET_BREED_DETAILS,
      variables: {
        id: data?.dog?.breed?.id,
        slug: data?.dog?.breed?.slug,
      },
    })

    breedDetails = dataBreed?.breed

    const { data: _breedPuppies } = await apolloClient.query({
      fetchPolicy: 'no-cache',
      query: PUPPY_SEARCH,
      variables: {
        breedSlug: data?.dog?.breed?.slug,
        limit: 20,
      },
    })
    allBreedPuppies = _breedPuppies

    if (data?.dog === null || !params) {
      return {
        redirect: {
          destination: '/search',
          statusCode: 301,
        },
      }
    }
  } catch (err) {
    console.error(err)
    throw err
  }
  return {
    props: {
      dataDog,
      breedDetails,
      allBreedPuppies,
    },
    revalidate: 10, // Revalidate at most once every 10 seconds
  }
}
